/**
 * @file AdminApp.tsx
 *
 * Entry point for the Online Shop admin. Owns the client-side router and maps
 * each shop section to its screen under the shared {@link AdminLayout}. The
 * route table is exported separately as {@link AdminRoutes} so tests can mount
 * it under a `MemoryRouter`.
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AdminLayout } from './layout/AdminLayout';
import { DEFAULT_SHOP_PATH } from './nav-items';
import { ProductsView } from './products/ProductsView';
import { ProductFormScreen } from './products/form/ProductFormScreen';
import { CategoriesView } from './categories/CategoriesView';
import { CategoryFormScreen } from './categories/form/CategoryFormScreen';
import { OrdersView } from './orders/OrdersView';
import { CouponsView } from './coupons/CouponsView';
import { CouponFormScreen } from './coupons/form/CouponFormScreen';
import { ShippingView } from './shipping/ShippingView';
import { CustomShippingFormScreen } from './shipping/custom-shipping/CustomShippingFormScreen';
import { LocalPickupFormScreen } from './shipping/local-pickup/LocalPickupFormScreen';
import { PaymentsView } from './payments/PaymentsView';
import { ShorePayConnectScreen } from './payments/shore-pay/ShorePayConnectScreen';
import { ManualPaymentScreen } from './payments/manual-payment/ManualPaymentScreen';
import { VatScreen } from './payments/vat/VatScreen';
import { SettingsView } from './settings/SettingsView';

/**
 * The shop admin route table (router-agnostic, for composing under any router).
 *
 * @returns The routed shop sections nested under {@link AdminLayout}.
 */
export function AdminRoutes() {
  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route
          index
          element={<Navigate to={DEFAULT_SHOP_PATH} replace />}
        />
        <Route path="products">
          <Route index element={<ProductsView />} />
          <Route path="new" element={<ProductFormScreen mode="create" />} />
          <Route path=":id/edit" element={<ProductFormScreen mode="edit" />} />
          <Route
            path=":id/duplicate"
            element={<ProductFormScreen mode="duplicate" />}
          />
        </Route>
        <Route path="categories">
          <Route index element={<CategoriesView />} />
          <Route path="new" element={<CategoryFormScreen mode="create" />} />
          <Route path=":id/edit" element={<CategoryFormScreen mode="edit" />} />
        </Route>
        <Route path="orders">
          <Route index element={<OrdersView />} />
        </Route>
        <Route path="coupons">
          <Route index element={<CouponsView />} />
          <Route path="new" element={<CouponFormScreen mode="create" />} />
          <Route path=":id/edit" element={<CouponFormScreen mode="edit" />} />
        </Route>
        <Route path="shipping">
          <Route index element={<ShippingView />} />
          <Route
            path="custom-shipping/new"
            element={<CustomShippingFormScreen mode="create" />}
          />
          <Route
            path="custom-shipping/:zoneId/edit"
            element={<CustomShippingFormScreen mode="edit" />}
          />
          <Route
            path="local-pickup/new"
            element={<LocalPickupFormScreen mode="create" />}
          />
          <Route
            path="local-pickup/:id/edit"
            element={<LocalPickupFormScreen mode="edit" />}
          />
        </Route>
        <Route path="payments">
          <Route index element={<PaymentsView />} />
          <Route path="shore-pay/connect" element={<ShorePayConnectScreen />} />
          <Route path="manual" element={<ManualPaymentScreen />} />
          <Route path="vat" element={<VatScreen />} />
        </Route>
        <Route path="settings" element={<SettingsView />} />
        <Route path="*" element={<Navigate to={DEFAULT_SHOP_PATH} replace />} />
      </Route>
    </Routes>
  );
}

/**
 * The shop admin mounted under a browser router.
 *
 * @returns The full admin app.
 */
export function AdminApp() {
  return (
    <BrowserRouter>
      <AdminRoutes />
    </BrowserRouter>
  );
}

export default AdminApp;
